import { Link, useLocation } from '@remix-run/react';
import FacebookLogo from '~/assets/svgs/facebookLogo';
import LinkedInLogo from '~/assets/svgs/linkedIn';
import XLogo from '../assets/twitter_x.png';
import '../styles/footer.css';
import { BuildInfo } from './BuildInfo/BuildInfo';
import { useEffect, useState } from 'react';
import { auth } from '../utils/auth/firebase-service';
import { DebugToolsModal } from './DebugToolsModal';
import '../styles/DebugOverlay.css';
import { isDevOrTestEnv } from '../utils/env';
import { debugGetManaged, debugSetManaged } from '../utils/ServerRequest';

export default function Footer() {
  const location = useLocation();
  const [textColor, setTextColor] = useState('#000');

  // Get the current year for copyright
  const startYear = 2024;
  const currentYear = new Date().getFullYear();
  const yearDisplay =
    currentYear > startYear ? `${startYear}-${currentYear}` : startYear;

  // Use a color detection approach
  useEffect(() => {
    const detectBackgroundColor = () => {
      // Get the computed background color of the footer
      const footer = document.querySelector('footer');
      if (footer) {
        const bgColor = window.getComputedStyle(footer).backgroundColor;

        // Parse the RGB values
        const rgb = bgColor.match(/\d+/g);
        if (rgb && rgb.length >= 3) {
          // Calculate brightness using the formula (0.299*R + 0.587*G + 0.114*B)
          const brightness =
            (0.299 * parseInt(rgb[0]) +
              0.587 * parseInt(rgb[1]) +
              0.114 * parseInt(rgb[2])) /
            255;

          // If brightness is greater than 0.5, background is light, use dark text
          setTextColor('#000');
        }
      }
    };

    detectBackgroundColor();

    // Add a resize listener to recalculate if the layout changes
    window.addEventListener('resize', detectBackgroundColor);

    return () => {
      window.removeEventListener('resize', detectBackgroundColor);
    };
  }, [location.pathname]);

  // Managed mode debug state for dev/test
  const [debugManaged, setDebugManaged] = useState(false);
  const [loadingManaged, setLoadingManaged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDebugAction, setPendingDebugAction] = useState<
    null | 'enable' | 'disable'
  >(null);

  // Fetch managed state on mount and when user logs in
  useEffect(() => {
    if (!isDevOrTestEnv() || !debugGetManaged) return;
    let unsub: any;
    const fetchManaged = async () => {
      setLoadingManaged(true);
      try {
        await auth.authStateReady?.(); // Wait for auth state if available
        if (typeof debugGetManaged === 'function') {
          const managed = await debugGetManaged();
          setDebugManaged(managed);
        }
      } catch {
        // Optionally set error here if you want to show on load
      } finally {
        setLoadingManaged(false);
      }
    };
    fetchManaged();
    // Listen for login/logout
    if (auth.onAuthStateChanged) {
      unsub = auth.onAuthStateChanged(() => {
        fetchManaged();
      });
    }
    return () => {
      if (unsub) unsub();
    };
  }, []);

  // Handler to toggle managed mode and update backend
  const handleToggleManaged = async () => {
    const newVal = !debugManaged;
    setDebugManaged(newVal);
    setError(null);
    if (isDevOrTestEnv() && debugSetManaged) {
      setPendingDebugAction(newVal ? 'enable' : 'disable');
      try {
        await debugSetManaged(newVal);
        // Show overlay for a short moment before reload
        setTimeout(() => {
          window.location.reload();
        }, 700);
      } catch (e: any) {
        setError(e?.message || 'Failed to update managed mode');
        setDebugManaged(!newVal); // revert toggle
        setPendingDebugAction(null);
      }
    }
  };

  return (
    <footer
      className={`${location.pathname === '/projects/list' && 'uploadsFooter'}`}
      style={{ position: 'relative' }}
    >
      {/* Debug modal in bottom right above BuildInfo */}
      {isDevOrTestEnv() && (
        <div
          style={{ position: 'fixed', right: 10, bottom: 60, zIndex: 10001 }}
        >
          <DebugToolsModal
            isManaged={debugManaged}
            onToggleManaged={handleToggleManaged}
            loading={loadingManaged}
          />
          {error && (
            <div
              style={{
                background: '#fff0f0',
                color: '#b00',
                border: '1px solid #b00',
                borderRadius: 6,
                padding: 12,
                marginTop: 8,
                minWidth: 220,
                maxWidth: 260,
                textAlign: 'center',
                fontSize: 13,
              }}
            >
              <strong>Error:</strong> {error}
              <button
                style={{
                  marginLeft: 12,
                  padding: '2px 8px',
                  borderRadius: 4,
                  border: '1px solid #b00',
                  background: '#fff',
                  color: '#b00',
                  cursor: 'pointer',
                }}
                onClick={() => setError(null)}
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}
      {/* Fullscreen overlay for debug action */}
      {pendingDebugAction && (
        <div className="debug-fullscreen-overlay">
          {pendingDebugAction === 'enable'
            ? 'Enabling Managed Mode...'
            : 'Disabling Managed Mode...'}
          <div className="debug-loader" />
        </div>
      )}
      <div className="rowArrangement footer">
        <div id="logoFooter">
          <p id="logoText" style={{ color: textColor }}>
            &copy; BLUESKEYE AI {yearDisplay} | All Rights Reserved
          </p>
        </div>
        <div id="footerButtonGroup">
          <Link to={`https://uk.linkedin.com/company/blueskeye-ai`}>
            <LinkedInLogo fill={textColor} />
          </Link>
          <Link to={`https://x.com/blueskeyeai`}>
            <img
              src={XLogo}
              alt="X Logo"
              style={{
                width: '38px',
                filter: textColor === '#fff' ? 'invert(1)' : 'none',
              }}
              onError={(e) => {
                e.currentTarget.onerror = null; // Prevents infinite loop if the fallback also fails
              }}
            />
          </Link>
          <Link to={`https://www.facebook.com/BlueskeyeAI`}>
            <FacebookLogo fill={textColor} />
          </Link>
        </div>
      </div>
      {/* BuildInfo component with its original styling */}
      <BuildInfo />
    </footer>
  );
}
